import { useCombobox } from 'downshift'
import { type FC, type InputHTMLAttributes, useCallback, useEffect, useState } from 'react'
import classnames from 'classnames'
import debounce from 'lodash/debounce'
import { type FieldErrorsImpl, useController, useFormContext } from 'react-hook-form'
import SuggestionText from '../../../shared/SuggestionText'
import TextInputError from '../../../shared/TextInputError'
import { useAnalytics } from '../../../../shared/context/Analytics'
import { P3 } from '../../../../shared/compass-ui'
import FadeAndCollapse from '../../../../shared/components/FadeAndCollapse'

export type NestedSuggestions = FieldErrorsImpl<{
  suggestions: {
    [x: string]: unknown
  }
}>

export const ADDRESS_ERROR_FIELDS = [
  'suggestions.streetAddress',
  'suggestions.city',
  'suggestions.state',
  'suggestions.zip',
  'streetAddress',
  'unit',
  'city',
  'state',
  'zip',
  'generalAddress',
]

const callSuggestionAddressApi = async (search: string) => {
  const searchComponent = `search=${encodeURIComponent(search)}`
  const url = `https://us-autocomplete-pro.api.smartystreets.com/lookup?${searchComponent}&key=8783109552894529`
  const response = await fetch(url)
  const data = await response.json()

  return data
}

interface AddressType {
  street_line: string
  secondary: string
  city: string
  state: string
  zipcode: string
}

interface AutocompleteProps extends InputHTMLAttributes<HTMLInputElement> {
  showHelperText: boolean
}

const Autocomplete: FC<AutocompleteProps> = ({ showHelperText, onBlur, onFocus }) => {
  const methods = useFormContext()
  const {
    clearErrors,
    setFocus,
    register,
    formState: { errors },
    setValue,
    getValues,
  } = methods
  const { field } = useController({ name: 'streetAddress' }) // get default field value

  const { streetAddress: streetAddressError } = errors
  const { suggestions } = errors as NestedSuggestions
  const { track, events } = useAnalytics()

  const [items, setItems] = useState<Array<AddressType>>([]) // NA is for the last item "Don't see your address?"

  useEffect(() => {
    if (track && suggestions) {
      if (typeof suggestions.streetAddress?.message === 'string') {
        track(events.ADDRESS_VALIDATION.AddressSuggestionStreetAddressView)
      }
    }
  }, [track, events, suggestions])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onInputValueChange = useCallback(
    debounce(async ({ inputValue }: { inputValue?: string }) => {
      if (!inputValue || inputValue.length < 2) return
      const { suggestions: smartySuggestions } = await callSuggestionAddressApi(inputValue)

      if (smartySuggestions === undefined || smartySuggestions.length === 0) {
        setItems([])
        return
      }

      // Do not show suggestions if street, city, state, and zip all match smarty's top suggestion
      const primarySuggestion = smartySuggestions[0] as AddressType
      const formValues = getValues()
      if (
        primarySuggestion.street_line?.toLowerCase() === formValues.streetAddress?.toLowerCase() &&
        primarySuggestion.city?.toLowerCase() === formValues.city?.toLowerCase() &&
        primarySuggestion.state?.toLowerCase() === formValues.state?.toLowerCase() &&
        primarySuggestion.zipcode === formValues.zip
      ) {
        setItems([])
        return
      }

      setItems([...smartySuggestions])
    }, 500),
    []
  )

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    setInputValue,
  } = useCombobox({
    id: 'address-autocomplete',
    onInputValueChange,
    items,
    itemToString(item) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      return item?.street_line ?? ''
    },
    onSelectedItemChange: ({ selectedItem: item }) => {
      setValue('streetAddress', item?.street_line)
      setValue('unit', item?.secondary)
      setValue('city', item?.city)
      setValue('state', item?.state)
      setValue('zip', item?.zipcode)
    },
  })

  const showOptions = isOpen && items.length !== 0
  const error =
    typeof streetAddressError?.message === 'string' || typeof errors?.general?.message === 'string'

  return (
    <>
      <label
        className="font-semibold text-ink-800 mt-4"
        htmlFor="street-address-input"
        {...getLabelProps()}
      >
        Street Address
      </label>
      <div>
        <input
          className={classnames({ error })}
          {...getInputProps({
            ...register('streetAddress'),
            value: field.value,
          })}
          data-testid="street-address-input"
          id="street-address-input"
          type="text"
          maxLength={50}
          onBlur={onBlur}
          onFocus={onFocus}
          autoComplete="address-line1"
        />
      </div>
      <ul
        {...getMenuProps()}
        className={`p-0 w-full border-blue-600 border-2 rounded-md bg-white shadow-md mt-s ${
          showOptions ? '' : 'hidden'
        }`}
      >
        {showOptions ? (
          items.map((item, index) => {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const { street_line, secondary, city, state, zipcode } = item
            const address = `${street_line} ${secondary} ${city} ${state} ${zipcode}`

            return (
              <li
                className={classnames(
                  highlightedIndex === index && 'bg-ink-300',
                  selectedItem === item && 'font-bold',
                  'py-2 px-3 shadow-sm flex flex-col first:rounded-t-md last:rounded-b-md'
                )}
                key={address}
                {...getItemProps({ item, index })}
              >
                <span className="font-sans text-ink-800">{address}</span>
              </li>
            )
          })
        ) : (
          <div />
        )}
      </ul>
      {typeof streetAddressError?.message === 'string' && (
        <TextInputError>{streetAddressError.message}</TextInputError>
      )}
      <FadeAndCollapse show={showHelperText}>
        <P3 className="mt-2">
          If approved, any changes made to your address will be made for all of your Mission Lane
          accounts.
        </P3>
      </FadeAndCollapse>
      {typeof suggestions?.streetAddress?.message === 'string' && (
        <SuggestionText>
          Did you mean{' '}
          <button
            className="underline text-blue-600"
            onClick={() => {
              track?.(events.ADDRESS_VALIDATION.AddressSuggestionStreetAddressAccept)
              clearErrors(ADDRESS_ERROR_FIELDS)
              setInputValue(suggestions?.streetAddress?.message ?? '')
              setValue('streetAddress', suggestions?.streetAddress?.message)
              setFocus('streetAddress')
            }}
            type="button"
          >
            {suggestions?.streetAddress?.message}
          </button>
          ?
        </SuggestionText>
      )}
    </>
  )
}

export default Autocomplete
